/* Atlas theme — vanilla JS, no dependencies */
(() => {
  'use strict';

  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));
  const T = () => window.theme || {};
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  /* ---------- Money ---------- */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const format = T().moneyFormat || '${{amount}}';
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const parts = (n / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? parts[0] + decimal + parts[1] : parts[0];
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(cents, 2, ' ', ',');
        default: return fmt(cents, 2);
      }
    });
  }

  /* ---------- Dialogs (size guide, filters, zoom) ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-dialog]');
    if (opener) {
      const d = document.getElementById(opener.dataset.openDialog);
      if (d && d.showModal) { e.preventDefault(); d.showModal(); }
      return;
    }
    if (e.target.closest('[data-close-dialog]')) {
      e.target.closest('dialog')?.close();
      return;
    }
    // click on backdrop closes
    if (e.target.tagName === 'DIALOG') e.target.close();
  });

  /* ---------- Gallery ---------- */
  function initGallery(scope) {
    const gallery = qs('[data-gallery]', scope);
    if (!gallery) return;
    const track = qs('[data-gallery-track]', gallery);
    if (!track) return;
    const dots = qsa('[data-dot]', gallery);
    const counter = qs('[data-gallery-index]', gallery);

    const slides = () => qsa('[data-media-id]', track);
    const setActive = (id) => {
      dots.forEach((d) => d.classList.toggle('is-active', d.dataset.dot === String(id)));
      const i = slides().findIndex((s) => s.dataset.mediaId === String(id));
      if (counter && i > -1) counter.textContent = i + 1;
    };

    gallery.goTo = (id) => {
      const slide = slides().find((s) => s.dataset.mediaId === String(id));
      if (!slide) return;
      if (isDesktop()) {
        // Desktop grid: bring the variant's image to the front
        if (track.firstElementChild !== slide) track.prepend(slide);
      } else {
        track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }
      setActive(id);
    };

    dots.forEach((d) => d.addEventListener('click', () => gallery.goTo(d.dataset.dot)));

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (isDesktop()) return;
        entries.forEach((en) => { if (en.isIntersecting) setActive(en.target.dataset.mediaId); });
      }, { root: track, threshold: 0.6 });
      slides().forEach((s) => io.observe(s));
    }

    // Zoom
    const zoom = qs('[data-zoom]', scope);
    if (zoom) {
      track.addEventListener('click', (e) => {
        const slide = e.target.closest('.gallery__slide--zoom');
        if (!slide || !zoom.showModal) return;
        zoom.showModal();
        const target = qs(`[data-zoom-id="${slide.dataset.mediaId}"]`, zoom);
        if (target) target.scrollIntoView({ block: 'start' });
      });
    }
  }

  /* ---------- Product ---------- */
  function initProduct(section) {
    if (section.dataset.ready) return;
    section.dataset.ready = 'true';
    initGallery(section);

    const form = qs('[data-product-form]', section);
    if (!form) return;
    const idInput = qs('[data-variant-id]', form);
    const qtyInput = qs('[data-qty-input]', form);
    const picker = qs('[data-variant-picker]', section);
    const variants = picker ? JSON.parse(qs('[data-variants]', picker).textContent) : [];
    const strings = T().strings || {};

    qsa('[data-qty-minus],[data-qty-plus]', section).forEach((btn) => {
      btn.addEventListener('click', () => {
        const d = btn.hasAttribute('data-qty-plus') ? 1 : -1;
        qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + d);
      });
    });

    const groups = () => qsa('[data-option-group]', picker);
    const selected = () => groups().map((g) => qs('input:checked', g)?.value);

    // Mark values that can't be bought with the other current selections
    const markAvailability = () => {
      if (!picker) return;
      const sel = selected();
      groups().forEach((g, gi) => {
        qsa('input', g).forEach((input) => {
          const ok = variants.some((v) => v.available && v.options[gi] === input.value &&
            v.options.every((o, i) => i === gi || sel[i] === undefined || o === sel[i]));
          input.closest('label').classList.toggle('is-unavailable', !ok);
        });
      });
    };

    const setButtons = (variant) => {
      const ok = variant && variant.available;
      const label = !variant ? strings.unavailable : ok ? strings.addToCart : strings.soldOut;
      const main = qs('[data-add-button]', section);
      if (main) { main.disabled = !ok; const t = qs('[data-add-text]', main); if (t) t.textContent = label; }
      const sticky = qs('[data-sticky-button]', section);
      if (sticky) { sticky.disabled = !ok; sticky.textContent = label; }
    };

    const updatePrice = (v) => {
      qsa('[data-price]', section).forEach((p) => {
        const sale = v.compare_at_price > v.price;
        p.classList.toggle('price--sale', sale);
        const cur = qs('[data-price-current]', p);
        const cmp = qs('[data-price-compare]', p);
        const badge = qs('[data-price-badge]', p);
        if (cur) cur.textContent = formatMoney(v.price);
        if (cmp) { cmp.hidden = !sale; cmp.textContent = sale ? formatMoney(v.compare_at_price) : ''; }
        if (badge) {
          badge.hidden = !sale;
          badge.textContent = sale ? `${strings.save} ${Math.floor(((v.compare_at_price - v.price) * 100) / v.compare_at_price)}%` : '';
        }
      });
      qsa('[data-sticky-price]', section).forEach((el) => (el.textContent = formatMoney(v.price)));
      qsa('[data-sticky-variant]', section).forEach((el) => (el.textContent = v.title + ' · '));
    };

    if (picker) {
      picker.addEventListener('change', () => {
        const sel = selected();
        groups().forEach((g, i) => { const l = qs('[data-selected-label]', g); if (l) l.textContent = sel[i] || ''; });
        const v = variants.find((x) => x.options.every((o, i) => o === sel[i]));
        markAvailability();
        setButtons(v);
        if (!v) return;
        idInput.value = v.id;
        updatePrice(v);
        section.dispatchEvent(new CustomEvent('variant:price', { detail: { price: v.price } }));
        if (v.featured_media) qs('[data-gallery]', section)?.goTo?.(v.featured_media.id);
        if (section.hasAttribute('data-update-url')) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        }
      });
      markAvailability();
      const initial = variants.find((v) => String(v.id) === String(idInput.value));
      if (initial && initial.featured_media && new URL(location.href).searchParams.get('variant')) {
        requestAnimationFrame(() => qs('[data-gallery]', section)?.goTo?.(initial.featured_media.id));
      }
    }

    const bar = qs('[data-sticky-atc]', section);
    const mainBuy = qs('[data-main-buy]', section);
    if (bar && mainBuy && 'IntersectionObserver' in window) {
      const btn = qs('[data-sticky-button]', bar);
      new IntersectionObserver(([en]) => {
        const show = !en.isIntersecting && en.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        if (btn) btn.tabIndex = show ? 0 : -1;
      }).observe(mainBuy);
    }
  }

  /* ---------- Recommendations ---------- */
  function initRecommendations(scope = document) {
    qsa('[data-recommendations]', scope).forEach(async (el) => {
      if (el.dataset.loaded) return;
      el.dataset.loaded = 'true';
      try {
        const res = await fetch(el.dataset.url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = qs('[data-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim()) { el.innerHTML = fresh.innerHTML; initReveal(el); }
      } catch (e) { console.error(e); }
    });
  }

  /* ---------- Predictive search ---------- */
  function initPredictive() {
    qsa('[data-predictive-input]').forEach((input) => {
      const pop = input.closest('[data-search-pop]');
      const out = qs('[data-predictive-results]', pop);
      if (pop && pop.tagName === 'DETAILS') pop.addEventListener('toggle', () => { if (pop.open) setTimeout(() => input.focus(), 30); });
      const run = debounce(async () => {
        const q = input.value.trim();
        if (q.length < 2) { out.innerHTML = ''; return; }
        const url = `${T().routes.root}search/suggest?q=${encodeURIComponent(q)}&resources[type]=product,collection,page&resources[limit]=8&section_id=predictive-search`;
        try {
          const html = await (await fetch(url)).text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const sec = qs('#shopify-section-predictive-search', doc);
          out.innerHTML = sec ? sec.innerHTML : '';
        } catch (e) { out.innerHTML = ''; }
      }, 250);
      input.addEventListener('input', run);
    });
  }

  /* ---------- Collection filters ---------- */
  function initFacets(scope = document) {
    qsa('[data-facets-form]', scope).forEach((form) => {
      if (form.dataset.ready) return;
      form.dataset.ready = 'true';
      const go = () => {
        const params = new URLSearchParams(new FormData(form));
        for (const [k, v] of [...params]) if (v === '') params.delete(k);
        window.location.search = params.toString();
      };
      form.addEventListener('submit', (e) => { e.preventDefault(); go(); });
      qsa('[data-auto-submit]', form).forEach((el) => el.addEventListener('change', go));
    });
    // Sort select lives outside the drawer but belongs to the same form via form="" attribute
    qsa('[data-sort]', scope).forEach((sel) => {
      sel.addEventListener('change', () => {
        const url = new URL(location.href);
        url.searchParams.set('sort_by', sel.value);
        url.searchParams.delete('page');
        location.href = url.toString();
      });
    });
    // Desktop column toggle
    qsa('[data-cols-toggle]', scope).forEach((wrap) => {
      const grid = document.getElementById(wrap.dataset.colsToggle);
      if (!grid) return;
      const apply = (n) => {
        grid.style.setProperty('--cols', n);
        qsa('button', wrap).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.cols === String(n))));
      };
      try { const saved = localStorage.getItem('atlas-cols'); if (saved) apply(saved); } catch (e) {}
      qsa('button', wrap).forEach((b) => b.addEventListener('click', () => {
        apply(b.dataset.cols);
        try { localStorage.setItem('atlas-cols', b.dataset.cols); } catch (e) {}
      }));
    });
  }

  /* ---------- Tabs (featured collection) ---------- */
  function initTabs(scope = document) {
    qsa('[data-tabs]', scope).forEach((wrap) => {
      const tabs = qsa('[role="tab"]', wrap);
      tabs.forEach((tab) => tab.addEventListener('click', () => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', String(on));
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
      }));
    });
  }

  /* ---------- Lookbook ---------- */
  function initLookbook(scope = document) {
    qsa('[data-lookbook]', scope).forEach((lb) => {
      const activate = (i) => {
        qsa('[data-look]', lb).forEach((el) => el.classList.toggle('is-active', el.dataset.look === i));
      };
      qsa('.hotspot__dot', lb).forEach((dot) => {
        dot.addEventListener('click', () => {
          const i = dot.closest('[data-look]').dataset.look;
          activate(i);
          const item = qs(`.look-item[data-look="${i}"]`, lb);
          if (item && !isDesktop()) item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      });
      qsa('.look-item', lb).forEach((item) => item.addEventListener('mouseenter', () => activate(item.dataset.look)));
    });
  }

  /* ---------- Reveal ---------- */
  function initReveal(scope = document) {
    const els = qsa('.reveal:not(.is-in)', scope);
    if (!('IntersectionObserver' in window)) { els.forEach((e) => e.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach((e) => io.observe(e));
  }

  /* ---------- Cart ---------- */
  let lastFocus = null;
  function setCount(count) {
    qsa('[data-cart-count-text]').forEach((el) => (el.textContent = count));
    qsa('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = Number(count) === 0; });
  }
  function renderDrawer(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = qs('[data-cart-drawer]', doc);
    const current = qs('[data-cart-drawer]');
    if (fresh && current) { current.replaceWith(fresh); setCount(fresh.dataset.count); }
  }
  function openDrawer() {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) { window.location.href = T().routes.cart; return; }
    lastFocus = document.activeElement;
    document.body.classList.add('drawer-open');
    drawer.setAttribute('aria-hidden', 'false');
    setTimeout(() => qs('.drawer__panel', drawer)?.focus(), 50);
  }
  function closeDrawer() {
    document.body.classList.remove('drawer-open');
    qs('[data-cart-drawer]')?.setAttribute('aria-hidden', 'true');
    lastFocus?.focus?.();
  }
  async function changeLine(line, quantity) {
    qs('[data-cart-drawer]')?.classList.add('is-busy');
    try {
      const res = await fetch(`${T().routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (data.sections) renderDrawer(data.sections['cart-drawer']); else setCount(data.item_count);
    } catch (e) { console.error(e); }
    finally { qs('[data-cart-drawer]')?.classList.remove('is-busy'); }
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form || T().cartType !== 'drawer' || !qs('[data-cart-drawer]')) return;
    e.preventDefault();
    const buttons = [...qsa('[type="submit"]', form), ...(form.id ? qsa(`[form="${form.id}"]`) : [])];
    buttons.forEach((b) => { b.classList.add('is-loading'); b.setAttribute('aria-busy', 'true'); });
    const err = qs('[data-form-error]', form);
    if (err) err.hidden = true;
    const body = new FormData(form);
    body.append('sections', 'cart-drawer');
    body.append('sections_url', window.location.pathname);
    try {
      const res = await fetch(`${T().routes.cartAdd}.js`, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || T().strings.error);
      renderDrawer(data.sections && data.sections['cart-drawer']);
      openDrawer();
    } catch (ex) {
      if (err) { err.textContent = ex.message; err.hidden = false; } else alert(ex.message);
    } finally {
      buttons.forEach((b) => { b.classList.remove('is-loading'); b.removeAttribute('aria-busy'); });
    }
  });

  document.addEventListener('click', (e) => {
    const lineBtn = e.target.closest('[data-line-change]');
    if (lineBtn) { e.preventDefault(); changeLine(parseInt(lineBtn.dataset.line, 10), Math.max(0, parseInt(lineBtn.dataset.qty, 10))); return; }
    if (e.target.closest('[data-drawer-close]')) { closeDrawer(); return; }
    const cartLink = e.target.closest('[data-cart-link]');
    if (cartLink && T().cartType === 'drawer' && qs('[data-cart-drawer]')) { e.preventDefault(); openDrawer(); }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (document.body.classList.contains('drawer-open')) closeDrawer();
    qsa('details.mobile-nav[open], details.search-pop[open]').forEach((d) => d.removeAttribute('open'));
  });

  /* ---------- Header ---------- */
  function initHeader() {
    const wrap = qs('[data-header]');
    if (!wrap) return;
    const onScroll = () => wrap.classList.toggle('is-scrolled', window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    // Close search when clicking outside
    document.addEventListener('click', (e) => {
      qsa('details.search-pop[open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
      qsa('form[data-search-pop]').forEach((f) => { if (!f.contains(e.target)) { const r = qs('[data-predictive-results]', f); if (r) r.innerHTML = ''; } });
    });
  }

  function initAnnouncement(scope = document) {
    qsa('[data-announcement]', scope).forEach((bar) => {
      const items = qsa('.announcement__item', bar);
      if (items.length < 2 || bar.dataset.ready) return;
      bar.dataset.ready = 'true';
      let i = 0;
      setInterval(() => { items[i].classList.remove('is-active'); i = (i + 1) % items.length; items[i].classList.add('is-active'); }, 5000);
    });
  }


  /* ---------- Card swatches (swap image / variant) ---------- */
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('[data-swatch]');
    if (!sw) return;
    e.preventDefault();
    const card = sw.closest('[data-card]');
    if (!card) return;
    qsa('[data-swatch]', card).forEach((b) => b.classList.toggle('is-active', b === sw));
    const img = qs('[data-card-img]', card);
    if (img && sw.dataset.img) {
      img.removeAttribute('srcset');
      img.src = sw.dataset.img;
      card.classList.add('is-swapped');
    }
    if (sw.dataset.url) qsa('[data-card-link]', card).forEach((a) => (a.href = sw.dataset.url));
    const input = qs('[data-card-variant]', card);
    const add = qs('[data-card-add]', card);
    if (input && sw.dataset.variantId) input.value = sw.dataset.variantId;
    if (add) {
      const ok = sw.dataset.available === 'true';
      add.disabled = !ok;
      const label = qs('span', add);
      if (label) label.textContent = ok ? T().strings.addToCart : T().strings.soldOut;
    }
  });

  /* ---------- Carousels ---------- */
  function initCarousels(scope = document) {
    qsa('[data-carousel-nav]', scope).forEach((nav) => {
      const track = document.getElementById(nav.dataset.carouselNav);
      if (!track || nav.dataset.ready) return;
      nav.dataset.ready = 'true';
      const [prev, next] = qsa('button', nav);
      const update = () => {
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      };
      qsa('button', nav).forEach((b) => b.addEventListener('click', () => {
        track.scrollBy({ left: Number(b.dataset.dir) * track.clientWidth * 0.9, behavior: 'smooth' });
      }));
      track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 120));
      update();
    });
  }


  /* ---------- Countdowns ---------- */
  const pad = (n) => String(n).padStart(2, '0');
  function tickCountdowns() {
    const now = Date.now();
    qsa('[data-countdown]').forEach((el) => {
      const end = Date.parse(el.dataset.countdown);
      if (isNaN(end)) { el.hidden = true; return; }
      let diff = Math.max(0, Math.floor((end - now) / 1000));
      if (diff === 0) {
        const sec = el.closest('[data-hide-when-over]');
        if (sec) { sec.hidden = true; return; }
        if (el.dataset.expired) { const o = document.createElement('span'); o.className = 'countdown__over'; o.textContent = el.dataset.expired; el.replaceChildren(o); delete el.dataset.countdown; }
        else el.hidden = true;
        return;
      }
      const d = Math.floor(diff / 86400); diff %= 86400;
      const h = Math.floor(diff / 3600); diff %= 3600;
      const m = Math.floor(diff / 60); const sec = diff % 60;
      const set = (k, v) => { const n = qs(`[data-cd="${k}"]`, el); if (n) n.textContent = pad(v); };
      set('days', d); set('hours', h); set('minutes', m); set('seconds', sec);
      el.classList.add('is-ready');
    });
    qsa('[data-deadline]').forEach((li) => {
      const t = Date.parse(li.dataset.deadline);
      if (isNaN(t)) return;
      li.classList.toggle('is-past', t < now);
    });
    const next = qsa('[data-deadline]:not(.is-past)')[0];
    qsa('[data-deadline]').forEach((li) => li.classList.toggle('is-next', li === next));
  }

  /* ---------- Copy discount code ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn) return;
    try { await navigator.clipboard.writeText(btn.dataset.copy); } catch (err) {}
    const done = qs('[data-copied]', btn);
    if (done) { done.hidden = false; setTimeout(() => (done.hidden = true), 1800); }
  });

  /* ---------- Gift finder ---------- */
  function initGiftFinder(scope = document) {
    qsa('[data-gift-finder]', scope).forEach((f) => {
      const go = qs('[data-finder-go]', f);
      const update = () => {
        const who = qs('input[name^="who-"]:checked', f);
        const budget = qs('input[name^="budget-"]:checked', f);
        const params = new URLSearchParams();
        if (who && who.value) params.set('filter.p.tag', who.value);
        if (budget && budget.value) params.set('filter.v.price.lte', budget.value);
        const q = params.toString();
        go.href = f.dataset.base + (q ? (f.dataset.base.includes('?') ? '&' : '?') + q : '');
      };
      f.addEventListener('change', update);
      update();
    });
  }

  /* ---------- Cart gift options ---------- */
  document.addEventListener('input', debounce((e) => {
    const note = e.target.closest && e.target.closest('[data-cart-note]');
    if (!note) return;
    fetch(`${T().routes.cart}/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
  }, 500));
  document.addEventListener('change', (e) => {
    const box = e.target.closest && e.target.closest('[data-cart-attr]');
    if (!box) return;
    fetch(`${T().routes.cart}/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { [box.dataset.cartAttr]: box.checked ? 'Yes' : '' } }) });
  });

  /* ---------- Bundle tiers -> quantity ---------- */
  function initBreaks(scope = document) {
    qsa('[data-product-section]', scope).forEach((section) => {
      const breaks = qs('[data-qbreaks]', section);
      const qty = qs('[data-qty-input]', section);
      if (!breaks || !qty || breaks.dataset.ready) return;
      breaks.dataset.ready = 'true';
      const apply = () => { const c = qs('input:checked', breaks); if (c) qty.value = c.value; };
      breaks.addEventListener('change', apply);
      apply();
      section.addEventListener('variant:price', (ev) => {
        const price = ev.detail.price;
        qsa('input', breaks).forEach((r) => {
          const q = parseInt(r.dataset.qty, 10) || 0, d = parseInt(r.dataset.discount, 10) || 0;
          const base = price * q, total = base - Math.floor((base * d) / 100);
          const label = r.closest('label');
          const t = qs('[data-qb-total]', label), b = qs('[data-qb-base]', label);
          if (t) t.textContent = formatMoney(total);
          if (b) b.textContent = formatMoney(base);
        });
      });
    });
  }

  /* ---------- Init ---------- */
  function init(scope = document) {
    qsa('[data-product-section]', scope).forEach(initProduct);
    initRecommendations(scope);
    initFacets(scope);
    initTabs(scope);
    initLookbook(scope);
    initAnnouncement(scope);
    initReveal(scope);
    initCarousels(scope);
    initGiftFinder(scope);
    initBreaks(scope);
    tickCountdowns();
  }
  document.addEventListener('DOMContentLoaded', () => { init(); initHeader(); initPredictive(); setInterval(tickCountdowns, 1000); });
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  window.Atlas = { formatMoney, openDrawer, closeDrawer };
})();
